/**
 * 그룹 생성 모달 컴포넌트 (admin만)
 * - 그룹명, 코드, 설명, 최대 학생/강사 수 입력
 * - 코드 형식 검증 (영문+숫자만, 소문자 자동 변환)
 * - 중복 체크 (Backend에서 처리)
 */

import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Row, Col, message } from 'antd';
import { groupService } from '../../../services/groupService';
import type { GroupCreateRequest } from '../../../types/group';

const { TextArea } = Input;

interface GroupCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const GroupCreateModal: React.FC<GroupCreateModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  /**
   * 모달 닫기 처리
   */
  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  /**
   * 그룹 생성 제출
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const data: GroupCreateRequest = {
        name: values.name,
        code: values.code.toLowerCase(), // 소문자 변환
        description: values.description || undefined,
        max_students: values.max_students || 100,
        max_instructors: values.max_instructors || 10,
      };

      await groupService.createGroup(data);
      message.success('그룹이 생성되었습니다');
      handleClose();
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.detail) {
        message.error(error.response.data.detail);
      } else {
        message.error('그룹 생성 실패');
      }
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="새 그룹 생성"
      open={open}
      onOk={handleSubmit}
      onCancel={handleClose}
      confirmLoading={loading}
      okText="생성"
      cancelText="취소"
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
        autoComplete="off"
        initialValues={{
          max_students: 100,
          max_instructors: 10,
        }}
      >
        <Form.Item
          label="그룹명"
          name="name"
          rules={[
            { required: true, message: '그룹명을 입력하세요' },
            { min: 1, max: 100, message: '그룹명은 1-100자여야 합니다' },
          ]}
        >
          <Input placeholder="예: 강남학원" />
        </Form.Item>

        <Form.Item
          label="그룹 코드"
          name="code"
          rules={[
            { required: true, message: '그룹 코드를 입력하세요' },
            { min: 3, max: 50, message: '그룹 코드는 3-50자여야 합니다' },
            {
              pattern: /^[a-zA-Z0-9]+$/,
              message: '영문과 숫자만 사용할 수 있습니다',
            },
          ]}
          extra="영문과 숫자만 사용 가능 (소문자 자동 변환). 예: gangnam, seocho123"
        >
          <Input placeholder="예: gangnam123" />
        </Form.Item>

        <Form.Item
          label="설명"
          name="description"
          rules={[{ max: 500, message: '설명은 500자 이하여야 합니다' }]}
        >
          <TextArea
            rows={3}
            placeholder="그룹 설명을 입력하세요 (선택사항)"
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="최대 학생 수"
              name="max_students"
              rules={[
                { required: true, message: '최대 학생 수를 입력하세요' },
                { type: 'number', min: 1, max: 1000, message: '1-1000 사이여야 합니다' },
              ]}
            >
              <InputNumber
                min={1}
                max={1000}
                style={{ width: '100%' }}
                placeholder="100"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              label="최대 강사 수"
              name="max_instructors"
              rules={[
                { required: true, message: '최대 강사 수를 입력하세요' },
                { type: 'number', min: 0, max: 100, message: '0-100 사이여야 합니다' },
              ]}
            >
              <InputNumber
                min={0}
                max={100}
                style={{ width: '100%' }}
                placeholder="10"
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
